import moment from 'moment';

function getRandomBrightHexColor() {
  const r = Math.floor(Math.random() * 156) + 100; // Minimum value of 100 for red
  const g = Math.floor(Math.random() * 156) + 100; // Minimum value of 100 for green
  const b = Math.floor(Math.random() * 156) + 100; // Minimum value of 100 for blue
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Processes and formats podcast ranking data for chart visualization
 * @param {Array} data - Raw podcast ranking data
 * @param {string} chosenCountry - Selected country to filter data
 * @param {string} activeRange - Time range to display (e.g., '60d')
 * @param {Array} episodes - Episode data for annotations
 * @param {boolean} isMobile - Flag for mobile view
 * @param {Object} customDateRange - Custom date range with startDate and endDate
 * @returns {Object} Formatted chart data with datasets and labels
 */
export const getSetupTopPodcastsChart = (
  data,
  chosenCountry,
  activeRange = '60d',
  episodes,
  isMobile,
  customDateRange = null
) => {
  // Convert range string to number of days or use custom date range
  const daysToShow = parseInt(activeRange);
  
  // Filter data for selected country (handle UK/United Kingdom variants)
  let filteredData = data.filter(({ country }) => {
    if (country === chosenCountry) return true;
    // Handle UK/United Kingdom variants
    if (country === 'UK' && chosenCountry === 'United Kingdom') return true;
    if (country === 'United Kingdom' && chosenCountry === 'UK') return true;
    return false;
  });

  // Calculate cutoff date based on custom date range or active range
  let cutoffDate, endDate;
  if (customDateRange) {
    cutoffDate = moment(customDateRange.startDate);
    endDate = moment(customDateRange.endDate);
  } else {
    cutoffDate = moment().subtract(daysToShow, 'days');
    endDate = moment();
  }

    // Filter history ranks to only include data within date range and sort by date
  filteredData = filteredData.map((item) => ({
    ...item,
    historyRank: item.historyRank
      .filter((rank) => {
        const rankDate = moment(rank.date);
        if (customDateRange) {
          // For custom date range, filter between start and end dates
          return rankDate.isSameOrAfter(cutoffDate, 'day') && rankDate.isSameOrBefore(endDate, 'day');
        } else {
          // For standard ranges, filter after cutoff date
          return moment(rankDate.format('YYYY-MM-DD')).isAfter(cutoffDate);
        }
      })
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()),
  }));

  // Generate labels for the date range
  const labels = [];
  if (customDateRange) {
    // For custom date range, generate labels between start and end dates
    const daysDiff = endDate.diff(cutoffDate, 'days') + 1;
    for (let i = 0; i < daysDiff; i++) {
      const date = moment(cutoffDate).add(i, 'days');
      labels.push(date.format('MMM DD'));
    }
  } else {
    // For standard ranges, generate labels from cutoff date to now
    for (let i = 0; i < daysToShow; i++) {
      const date = moment(cutoffDate).add(i + 1, 'days');
      labels.push(date.format('MMM DD'));
    }
  }

  // Define base colors for chart lines
  const colors = ['#ff4e00', '#ff9003', '#1999c4'];

  // Sort data by history length to ensure consistent ordering
  const sortedData = [...filteredData].sort((a, b) => b.historyRank.length - a.historyRank.length);

  // Generate unique genres with assigned colors
  const genres = [
    ...new Set(
      sortedData.reduce((accum, item, i) => {
        if (!colors[i]) colors[i] = getRandomBrightHexColor();
        if (!item.historyRank.length) {
          return accum;
        }
        return [
          ...accum,
          {
            name: item.genre,
            color: colors[i],
          },
        ];
      }, [])
    ),
  ].sort((a, b) => {
    if (a.name === 'Top Podcasts') return -1;
    if (b.name === 'Top Podcasts') return 1;
    return 0;
  });

  // Track if episode data has been added to prevent duplicates
  let episodeIsAdded = false;

  // Generate chart datasets with podcast ranking data
  const chartData = sortedData.reduce(
    (accum, item, i) => {
      const color = colors.shift();
      if (!item) {
        return accum;
      }

      // Map ranking data to chart points with metadata
      const dataPoints = item.historyRank.map(({ rank, date }, i) => {
        let matchingEpisode = null;
        if (!episodeIsAdded && !item.hidden) {
          matchingEpisode = episodes?.find(
            (episode) =>
              moment(episode.pubDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
          );
        }
        return {
          x: moment(date).format('MMM DD'),
          y: rank,
          episode: matchingEpisode,
          isLastRank: item.historyRank.length - 1 === i && !item.hidden && !episodeIsAdded,
          podcast: !item.hidden
            ? {
                name: item.name,
                rank: rank,
                prevRank: item.historyRank[i - 1]?.rank || null,
                images: item.images,
                genre: item.genre,
                country: item.country,
              }
            : null,
        };
      });

      // Add dataset if it contains data points
      if (dataPoints.length > 0) {
        accum.datasets.push({
          pointHitRadius: 10,
          label: item.genre,
          data: dataPoints,
          backgroundColor: color,
          borderColor: color,
          hidden: !!item.hidden,
        });
        episodeIsAdded = episodeIsAdded || !item.hidden;
      }

      return accum;
    },
    {
      labels,
      datasets: [],
      genres,
    }
  );

  // Add padding for better visualization on non-mobile devices
  const maxLength = chartData.datasets.reduce(
    (max, { data }) => Math.max(max, data?.length || 0),
    0
  );
  const longestDataset = Math.max(daysToShow, maxLength);
  // Add padding points for smoother visualization if dataset is large enough
  if (longestDataset > 10 && chartData.datasets.length > 0 && !isMobile) {
    const paddingCount = Math.round(longestDataset / 10) + 1;

    // Add padding to each dataset
    chartData.datasets = chartData.datasets.map((dataset) => {
      if (!dataset.data?.length) return;
      const firstDate = moment(dataset.data[0].x, 'MMM DD');
      const lastDate = moment(dataset.data[dataset.data.length - 1].x, 'MMM DD');

      let startPadding = [];
      let endPadding = [];

      // Add start padding if dates align with longest dataset
      if (firstDate.isSame(moment(chartData.labels[0], 'MMM DD'), 'day')) {
        startPadding = Array(paddingCount)
          .fill()
          .map((_, i) => {
            const firstRealY = +dataset.data[0].y;
            const variation = Math.floor(Math.random() * 4) + 1;
            return {
              x: `${firstDate
                .clone()
                .subtract(paddingCount - i, 'days')
                .format('MMM DD')} : f`,
              y: firstRealY + variation,
            };
          });
      }

      // Add end padding if dates align with longest dataset
      if (lastDate.isSame(moment(chartData.labels[chartData.labels.length - 1], 'MMM DD'), 'day')) {
        endPadding = Array(paddingCount)
          .fill()
          .map((_, i) => {
            const lastRealY = +dataset.data[dataset.data.length - 1].y;
            const variation = Math.floor(Math.random() * 4) + 1;
            return {
              x: `${lastDate
                .clone()
                .add(i + 1, 'days')
                .format('MMM DD')} : f`,
              y: lastRealY + variation,
            };
          });
      }

      return {
        ...dataset,
        data: [...startPadding, ...dataset.data, ...endPadding],
      };
    });

    // Update labels to include padding dates
    const firstLabel = moment(chartData.labels[0], 'MMM DD');
    const lastLabel = moment(chartData.labels[chartData.labels.length - 1], 'MMM DD');

    // Generate padding labels for start and end
    const startLabels = Array(paddingCount)
      .fill()
      .map(
        (_, i) =>
          `${firstLabel
            .clone()
            .subtract(paddingCount - i, 'days')
            .format('MMM DD')} : f`
      );

    const endLabels = Array(paddingCount)
      .fill()
      .map(
        (_, i) =>
          `${lastLabel
            .clone()
            .add(++i, 'days')
            .format('MMM DD')} : f`
      );

    // Combine all labels
    chartData.labels = [...startLabels, ...chartData.labels, ...endLabels];
  }

  return chartData;
};

export const findCountryWithTodayData = (data, sortedCountries, isDemoMode = false) => {
  if (!isDemoMode) {
    return sortedCountries[0];
  }

  const usData = data.find((item) => item.country === 'United States');
  if (usData) {
    const lastHistoryDate = usData.historyRank[usData.historyRank.length - 1].date;
    const isUSToday = moment(lastHistoryDate).isSame(moment(), 'day');

    if (isUSToday) {
      return 'United States';
    }
  }

  const todayData = data.find((item) => {
    if (item.country === 'United States') return false;
    const lastHistoryDate = item.historyRank[item.historyRank.length - 1].date;
    return moment(lastHistoryDate).isSame(moment(), 'day');
  });

  if (todayData) {
    return todayData.country;
  }

  return sortedCountries[0];
};
